import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { appointmentApi } from "@/api/appointment";
import { useAuth } from "@/contexts/AuthContext";

export type BookingHospitalOption = { id: string; name: string };

/**
 * Hospitals the current user may book appointments at (multi-site chain tenants).
 */
export function useBookingHospitals() {
  const { user, activeHospitalId, isChainAdmin } = useAuth();

  const { data: fetched = [], isLoading } = useQuery({
    queryKey: ["booking-hospitals"],
    queryFn: () => appointmentApi.getBookingHospitals(),
    staleTime: 60_000,
  });

  const hospitals: BookingHospitalOption[] = useMemo(() => {
    if (isChainAdmin && (user?.hospitals?.length ?? 0) > 0) {
      return user!.hospitals!.map((h) => ({ id: h.id, name: h.name }));
    }
    return fetched.filter((h) => h?.id && h?.name);
  }, [isChainAdmin, user?.hospitals, fetched]);

  const needsHospitalPicker = hospitals.length > 1;

  const defaultHospitalId = useMemo(() => {
    if (activeHospitalId && hospitals.some((h) => h.id === activeHospitalId)) {
      return activeHospitalId;
    }
    if (user?.hospitalId && hospitals.some((h) => h.id === user.hospitalId)) {
      return user.hospitalId;
    }
    return hospitals[0]?.id ?? "";
  }, [activeHospitalId, user?.hospitalId, hospitals]);

  return {
    hospitals,
    needsHospitalPicker,
    defaultHospitalId,
    isLoading,
  };
}
